import React from 'react';
/* eslint-disable react/no-unescaped-entities */
import { motion } from 'framer-motion';
import { Activity, EyeOff, BellOff, Users, Database, Sparkles, ArrowRight } from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };

const POINTS = [
    { icon: Activity, title: 'Suivi vendeur irrégulier', desc: 'Les propriétaires sont laissés dans le flou après la signature du mandat.' },
    { icon: EyeOff, title: 'Manque de visibilité', desc: "En tant que manager, vous n'avez aucune idée des actions réellement effectuées sur le terrain." },
    { icon: BellOff, title: 'Relances oubliées', desc: 'Des opportunités de vente se perdent par manque de rigueur et de suivi systématique.' },
    { icon: Users, title: 'Expérience client hétérogène', desc: "Chaque agent a sa méthode, l'image de marque de l'agence en souffre." },
    { icon: Database, title: 'Données dispersées', desc: 'Mandats, comptes-rendus de visite, échanges vendeur : aucun acteur n\'a la vision complète.' },
];

/**
 * ConvProblem — Section 2. Mirror the agency director's daily frustrations.
 * Iter162 : add the « hairdresser theory » lead-in (vendeur perspective)
 * without devaluing agencies.
 */
export const ConvProblem = ({ onDemoClick }) => {
    return (
        <section className="relative py-20 md:py-32" data-testid="problem-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <motion.span
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.5 }}
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[10px] font-medium tracking-widest text-[#A19DAB] uppercase"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                    Le quotidien d'une agence
                </motion.span>

                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.6 }}
                    className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white max-w-3xl leading-[1.1]"
                    style={DISPLAY}
                >
                    Vos agents perdent du temps.{' '}
                    <span className="text-[#6E6A7A]">Vos vendeurs perdent patience.</span>
                </motion.h2>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {POINTS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <motion.div
                                key={p.title}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ duration: 0.5, delay: i * 0.06 }}
                                data-testid={`problem-card-${i}`}
                                className="group rounded-2xl border border-white/[0.06] bg-[#0C0A10] p-7 hover:border-white/[0.12] transition-colors"
                            >
                                <div className="w-11 h-11 rounded-xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center">
                                    <Icon className="w-5 h-5 text-[#FF914D]" strokeWidth={1.8} />
                                </div>
                                <h3 className="mt-5 text-lg font-semibold text-white" style={DISPLAY}>{p.title}</h3>
                                <p className="mt-2 text-sm text-[#A19DAB] leading-relaxed">{p.desc}</p>
                            </motion.div>
                        );
                    })}

                    {/* Carte passerelle — transforme la frustration en action (comble la 6e case) */}
                    <motion.button
                        type="button"
                        onClick={onDemoClick}
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 0.5, delay: POINTS.length * 0.06 }}
                        data-testid="problem-cta-card"
                        className="group relative overflow-hidden rounded-2xl border border-[#FF914D]/30 bg-gradient-to-br from-[#681793]/25 via-[#15101f] to-[#FF914D]/15 p-7 text-left transition-all hover:border-[#FF914D]/60 hover:scale-[1.01]"
                    >
                        <div className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#FF914D]/20 blur-[80px]" />
                        <div className="relative flex h-full flex-col">
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shadow-lg shadow-[#681793]/40">
                                <Sparkles className="w-5 h-5 text-white" strokeWidth={1.8} />
                            </div>
                            <h3 className="mt-5 text-lg font-semibold text-white" style={DISPLAY}>Et si tout ça disparaissait&nbsp;?</h3>
                            <p className="mt-2 text-sm text-[#C9C5D2] leading-relaxed">
                                Homly structure vos process, pilote vos agents et rassure vos vendeurs — automatiquement.
                            </p>
                            <span className="mt-auto pt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#FF914D]">
                                Demander une démonstration
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                            </span>
                        </div>
                    </motion.button>
                </div>
            </div>
        </section>
    );
};
